import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { 
  getBlogPosts, 
  createBlogPost, 
  updateBlogPost, 
  deleteBlogPost,
  getBlogCategories,
  getBlogTags,
  publishBlogPost,
  getBlogStatistics
} from '../api/api';
import './AdminBlog.css';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [existingTags, setExistingTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags_input: '',
    featured_image: null,
    image_caption: '',
    image_credit: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    reading_time: 5,
    post_type: 'article',
    status: 'draft',
    featured: false
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [showSeoSettings, setShowSeoSettings] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { user, isAuthenticated, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("🔍 AdminBlog Mounted - Auth Debug:");
    console.log("User:", user);
    console.log("isAuthenticated:", isAuthenticated);
    console.log("Token exists:", !!token);
    console.log("User role:", user?.role);
    
    // Check user role and authentication - similar to AdminDashboard
    const checkUserRole = () => {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      console.log("👤 Current user from localStorage:", storedUser);
      
      if (!storedUser || (storedUser.role !== 'ADMIN' && storedUser.role !== 'WRITER')) {
        console.log("❌ User not authorized for AdminBlog");
        setError("You need to be logged in as an ADMIN or WRITER to access blog management.");
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return false;
      }
      
      // Also check if AuthContext user matches
      if (!user || (user.role !== 'ADMIN' && user.role !== 'WRITER')) {
        console.log("❌ AuthContext user not authorized");
        setError("Your session may have expired. Please log in again.");
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
        return false;
      }
      
      return true;
    };
    
    if (checkUserRole()) {
      fetchData();
      fetchStatistics();
    }
  }, [user, navigate, logout]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log("📋 Fetching blog data with auth token...");
      console.log("Token:", token ? token.substring(0, 20) + "..." : "No token");
      
      const [postsData, catsData, tagsData] = await Promise.all([
        getBlogPosts(),
        getBlogCategories(),
        getBlogTags()
      ]);
      
      console.log("Posts data received:", postsData);
      console.log("Categories data received:", catsData);
      console.log("Tags data received:", tagsData);
      
      setPosts(Array.isArray(postsData) ? postsData : postsData.results || []);
      setCategories(catsData);
      setExistingTags(tagsData);
      
      // If no category selected and categories exist, set first one
      if (catsData.length > 0 && !formData.category) {
        setFormData(prev => ({
          ...prev,
          category: catsData[0].id
        }));
      }
    } catch (error) {
      console.error('Error fetching blog data:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error("Authentication error - clearing local storage");
        setError("Your session has expired. Please log in again.");
        localStorage.removeItem("user");
        localStorage.removeItem("authToken");
        localStorage.removeItem("isAuthenticated");
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(`Failed to fetch blog data: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const stats = await getBlogStatistics();
      console.log("Blog statistics fetched:", stats);
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    if (formData.reading_time && (formData.reading_time < 1 || formData.reading_time > 60)) {
      errors.reading_time = 'Reading time must be between 1 and 60 minutes';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    console.log("=== CREATE POST DEBUG ===");
    console.log("Current user:", user);
    console.log("User role:", user?.role);
    console.log("Token available:", !!token);
    console.log("Form data:", formData);
    console.log("Category value:", formData.category);
    console.log("Type of category:", typeof formData.category);
    
    // Validate user role
    if (user?.role !== 'ADMIN' && user?.role !== 'WRITER') {
      setError('You do not have permission to create blog posts.');
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      setError('Please fix the form errors before submitting.');
      return;
    }
    
    try {
      // Create FormData object
      const postFormData = new FormData();
      
      // Add all text fields
      Object.keys(formData).forEach(key => {
        if (key === 'featured_image') {
          if (formData[key] instanceof File) {
            postFormData.append(key, formData[key]);
          }
        } else if (key === 'tags_input') {
          postFormData.append(key, formData[key] || '');
        } else if (key === 'featured') {
          postFormData.append(key, formData[key] ? 'true' : 'false');
        } else if (key === 'category') {
          // Ensure category is sent as string (Django expects string for PrimaryKeyRelatedField)
          postFormData.append(key, String(formData[key]));
        } else {
          postFormData.append(key, formData[key] || '');
        }
      });
      
      // Log what's being sent
      console.log("📤 FormData being sent:");
      for (let pair of postFormData.entries()) {
        const key = pair[0];
        const value = pair[1];
        if (value instanceof File) {
          console.log(`${key}: File - ${value.name}`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }
      
      await createBlogPost(postFormData);
      setShowCreateModal(false);
      resetForm();
      fetchData();
      fetchStatistics();
      setSuccess('Post created successfully!');
      setError('');
    } catch (error) {
      console.error('❌ Error creating post:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Your session has expired or you do not have permission to create posts. Please log in again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      // Handle validation errors from backend
      if (error && typeof error === 'object') {
        const errorMessages = [];
        for (const [field, messages] of Object.entries(error)) {
          if (Array.isArray(messages)) {
            errorMessages.push(`${field}: ${messages.join(', ')}`);
          } else {
            errorMessages.push(`${field}: ${messages}`);
          }
        }
        setError(`Server validation errors:\n\n${errorMessages.join('\n')}`);
      } else {
        setError('Failed to create post. Please check the console for details.');
      }
    }
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    
    if (!currentPost) return;
    
    console.log("=== UPDATE POST DEBUG ===");
    console.log("Current user:", user);
    console.log("User role:", user?.role);
    console.log("Post ID:", currentPost.id);
    console.log("Form data:", formData);
    
    // Validate user role
    if (user?.role !== 'ADMIN' && user?.role !== 'WRITER') {
      setError('You do not have permission to update blog posts.');
      return;
    }
    
    // Check if writer is trying to edit someone else's post
    if (user?.role === 'WRITER' && currentPost.author?.id !== user?.id) {
      setError('You can only edit your own posts.');
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      setError('Please fix the form errors before submitting.');
      return;
    }
    
    try {
      // Create FormData object
      const postFormData = new FormData();
      
      // Add all fields
      Object.keys(formData).forEach(key => {
        if (key === 'featured_image') {
          if (formData[key] instanceof File) {
            postFormData.append(key, formData[key]);
          }
        } else if (key === 'tags_input') {
          postFormData.append(key, formData[key] || '');
        } else if (key === 'featured') {
          postFormData.append(key, formData[key] ? 'true' : 'false');
        } else if (key === 'category') {
          postFormData.append(key, String(formData[key]));
        } else {
          postFormData.append(key, formData[key] || '');
        }
      });
      
      await updateBlogPost(currentPost.id, postFormData);
      setShowEditModal(false);
      resetForm();
      fetchData();
      fetchStatistics();
      setSuccess('Post updated successfully!');
      setError('');
    } catch (error) {
      console.error('Error updating post:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Your session has expired or you do not have permission to update posts. Please log in again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      setError('Failed to update post. Please try again.');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    // Validate user role
    if (user?.role !== 'ADMIN' && user?.role !== 'WRITER') {
      setError('You do not have permission to delete blog posts.');
      return;
    }
    
    try {
      await deleteBlogPost(postId);
      fetchData();
      fetchStatistics();
      setSuccess('Post deleted successfully!');
      setError('');
    } catch (error) {
      console.error('Error deleting post:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Your session has expired or you do not have permission to delete posts. Please log in again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      setError('Failed to delete post.');
    }
  };

  const handlePublishPost = async (postId) => {
    // Validate user role
    if (user?.role !== 'ADMIN' && user?.role !== 'WRITER') {
      setError('You do not have permission to publish blog posts.');
      return;
    }
    
    try {
      await publishBlogPost(postId);
      fetchData();
      fetchStatistics();
      setSuccess('Post published successfully!');
      setError('');
    } catch (error) {
      console.error('Error publishing post:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Your session has expired or you do not have permission to publish posts. Please log in again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      setError('Failed to publish post.');
    }
  };

  const handleEditClick = (post) => {
    // Check if user can edit this post (admin can edit all, writers can only edit their own)
    if (user?.role === 'WRITER' && post.author?.id !== user?.id) {
      setError('You can only edit your own posts.');
      return;
    }
    
    setCurrentPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category?.id || '',
      tags_input: post.tags?.map(tag => tag.name).join(', ') || '',
      featured_image: null,
      image_caption: post.image_caption || '',
      image_credit: post.image_credit || '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      reading_time: post.reading_time || 5,
      post_type: post.post_type || 'article',
      status: post.status || 'draft',
      featured: post.featured || false
    });
    setPreviewImage(post.featured_image);
    setShowEditModal(true);
    setFormErrors({});
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Clear error for this field
    setFormErrors(prev => ({ ...prev, [name]: undefined }));
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewImage(null);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: categories.length > 0 ? categories[0].id : '',
      tags_input: '',
      featured_image: null,
      image_caption: '',
      image_credit: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      reading_time: 5,
      post_type: 'article',
      status: 'draft',
      featured: false
    });
    setPreviewImage(null);
    setCurrentPost(null);
    setShowSeoSettings(false);
    setFormErrors({});
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'status-draft', text: 'Draft' },
      published: { class: 'status-published', text: 'Published' },
      archived: { class: 'status-archived', text: 'Archived' }
    };
    return badges[status] || badges.draft;
  };

  const suggestTags = (input) => {
    if (!input || input.length < 2) return [];
    const inputLower = input.toLowerCase();
    return existingTags
      .filter(tag => tag.name.toLowerCase().includes(inputLower))
      .slice(0, 5);
  };

  const addTagSuggestion = (tagName) => {
    const currentTags = formData.tags_input.split(',').map(t => t.trim()).filter(t => t);
    if (!currentTags.includes(tagName)) {
      const newTags = [...currentTags, tagName];
      setFormData(prev => ({
        ...prev,
        tags_input: newTags.join(', ')
      }));
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, category: value }));
    setFormErrors(prev => ({ ...prev, category: undefined }));
  };

  // Check if user can perform actions on a post
  const canEditPost = (post) => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    if (user.role === 'WRITER' && post.author?.id === user.id) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="admin-blog-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading blog dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-blog-container">
      {/* Error and Success Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-message">{error}</span>
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span className="alert-message">{success}</span>
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}
      
      {/* Header */}
      <div className="admin-blog-header">
        <div className="header-left">
          <h1>Blog Management</h1>
          <div className="user-info">
            <span className="user-role-badge">
              {user?.role === 'ADMIN' ? 'Administrator' : 'Writer'}
            </span>
            <span className="user-name">{user?.username}</span>
          </div>
        </div>
        <button 
          className="btn-create-post"
          onClick={() => {
            if (user?.role !== 'ADMIN' && user?.role !== 'WRITER') {
              setError('You do not have permission to create blog posts.');
              return;
            }
            setShowCreateModal(true);
            setFormErrors({});
            setError('');
          }}
        >
          + Create New Post
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="blog-stats">
          <div className="stat-card">
            <h3>Total Posts</h3>
            <p className="stat-number">{statistics.total_posts || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Published</h3>
            <p className="stat-number">{statistics.published_posts || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Total Views</h3>
            <p className="stat-number">{statistics.total_views || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Total Likes</h3>
            <p className="stat-number">{statistics.total_likes || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Comments</h3>
            <p className="stat-number">{statistics.total_comments || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Subscribers</h3>
            <p className="stat-number">{statistics.total_subscribers || 0}</p>
          </div>
        </div>
      )}

      {/* Posts Table */}
      <div className="posts-table-container">
        <table className="posts-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Status</th>
              <th>Author</th>
              <th>Published Date</th>
              <th>Views</th>
              <th>Likes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-posts">
                  No posts found. Create your first post!
                </td>
              </tr>
            ) : (
              posts.map(post => (
                <tr key={post.id}>
                  <td className="post-title-cell">
                    <Link to={`/blog/${post.id}`} target="_blank">
                      {post.title}
                    </Link>
                    {post.featured && <span className="featured-badge">Featured</span>}
                  </td>
                  <td>{post.category?.name || 'Uncategorized'}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(post.status).class}`}>
                      {getStatusBadge(post.status).text}
                    </span>
                  </td>
                  <td>
                    {post.author?.username || 'Unknown'}
                    {post.author?.id === user?.id && <span className="your-post"> (Yours)</span>}
                  </td>
                  <td>{formatDate(post.published_at)}</td>
                  <td>{post.views || 0}</td>
                  <td>{post.likes_count || 0}</td>
                  <td className="actions-cell">
                    {canEditPost(post) ? (
                      <>
                        <button className="btn-edit" onClick={() => handleEditClick(post)}>
                          Edit
                        </button>
                        {post.status !== 'published' && (
                          <button className="btn-publish" onClick={() => handlePublishPost(post.id)}>
                            Publish
                          </button>
                        )}
                        <button className="btn-delete" onClick={() => handleDeletePost(post.id)}>
                          Delete
                        </button>
                      </>
                    ) : (
                      <span className="no-permission">Read Only</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Post</h2>
              <button className="modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreatePost} className="post-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={formErrors.title ? 'field-error' : ''}
                    placeholder="Enter post title"
                  />
                  {formErrors.title && <div className="error-message">{formErrors.title}</div>}
                </div>
                
                <div className="form-group">
                  <label>Post Type</label>
                  <select name="post_type" value={formData.post_type} onChange={handleInputChange}>
                    <option value="article">Article</option>
                    <option value="news">News</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="case_study">Case Study</option>
                    <option value="feature">Feature</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Excerpt</label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Brief summary of the post..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows="10"
                    className={formErrors.content ? 'field-error' : ''}
                    required
                    placeholder="Write your post content here..."
                  />
                  {formErrors.content && <div className="error-message">{formErrors.content}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleCategoryChange}
                    className={formErrors.category ? 'field-error' : ''}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && <div className="error-message">{formErrors.category}</div>}
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tags_input"
                    value={formData.tags_input}
                    onChange={handleInputChange}
                    placeholder="Enter tags separated by commas (e.g., AI, Technology, Writing)"
                  />
                  <small>Separate tags with commas. Example: AI, Technology, Writing, Future Trends</small>
                  
                  {formData.tags_input && suggestTags(formData.tags_input).length > 0 && (
                    <div className="tag-suggestions">
                      <small>Suggestions:</small>
                      {suggestTags(formData.tags_input).map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          className="tag-suggestion"
                          onClick={() => addTagSuggestion(tag.name)}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Featured Image</label>
                  <input
                    type="file"
                    name="featured_image"
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  {previewImage && (
                    <div className="image-preview">
                      <img src={previewImage} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image Caption</label>
                  <input
                    type="text"
                    name="image_caption"
                    value={formData.image_caption}
                    onChange={handleInputChange}
                    placeholder="Caption for the featured image"
                  />
                </div>

                <div className="form-group">
                  <label>Image Credit</label>
                  <input
                    type="text"
                    name="image_credit"
                    value={formData.image_credit}
                    onChange={handleInputChange}
                    placeholder="Credit/source for the image"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reading Time (minutes)</label>
                  <input
                    type="number"
                    name="reading_time"
                    value={formData.reading_time}
                    onChange={handleInputChange}
                    min="1"
                    max="60"
                    className={formErrors.reading_time ? 'field-error' : ''}
                  />
                  {formErrors.reading_time && <div className="error-message">{formErrors.reading_time}</div>}
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                    />
                    Featured Post
                  </label>
                </div>
              </div>

              <div className="form-section">
                <div className="seo-header" onClick={() => setShowSeoSettings(!showSeoSettings)}>
                  <h3>SEO Settings</h3>
                  <span className="toggle-icon">{showSeoSettings ? '−' : '+'}</span>
                </div>
                
                {showSeoSettings && (
                  <div className="seo-content">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Title</label>
                        <input
                          type="text"
                          name="meta_title"
                          value={formData.meta_title}
                          onChange={handleInputChange}
                          placeholder="Title for search engines"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Description</label>
                        <textarea
                          name="meta_description"
                          value={formData.meta_description}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Description for search engines"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Keywords</label>
                        <input
                          type="text"
                          name="meta_keywords"
                          value={formData.meta_keywords}
                          onChange={handleInputChange}
                          placeholder="Keywords separated by commas"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">Create Post</button>
                <button type="button" className="btn-cancel" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {showEditModal && currentPost && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Post</h2>
              <button className="modal-close" onClick={() => { setShowEditModal(false); resetForm(); }}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleUpdatePost} className="post-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={formErrors.title ? 'field-error' : ''}
                  />
                  {formErrors.title && <div className="error-message">{formErrors.title}</div>}
                </div>
                
                <div className="form-group">
                  <label>Post Type</label>
                  <select name="post_type" value={formData.post_type} onChange={handleInputChange}>
                    <option value="article">Article</option>
                    <option value="news">News</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="case_study">Case Study</option>
                    <option value="feature">Feature</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Excerpt</label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows="10"
                    className={formErrors.content ? 'field-error' : ''}
                    required
                  />
                  {formErrors.content && <div className="error-message">{formErrors.content}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleCategoryChange}
                    className={formErrors.category ? 'field-error' : ''}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && <div className="error-message">{formErrors.category}</div>}
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tags_input"
                    value={formData.tags_input}
                    onChange={handleInputChange}
                    placeholder="Enter tags separated by commas"
                  />
                  <small>Separate tags with commas. Example: AI, Technology, Writing</small>
                  
                  {formData.tags_input && suggestTags(formData.tags_input).length > 0 && (
                    <div className="tag-suggestions">
                      <small>Suggestions:</small>
                      {suggestTags(formData.tags_input).map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          className="tag-suggestion"
                          onClick={() => addTagSuggestion(tag.name)}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Featured Image</label>
                  <input
                    type="file"
                    name="featured_image"
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  {previewImage && (
                    <div className="image-preview">
                      <img src={previewImage} alt="Preview" />
                      <small>Current image (upload new to replace)</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image Caption</label>
                  <input
                    type="text"
                    name="image_caption"
                    value={formData.image_caption}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Image Credit</label>
                  <input
                    type="text"
                    name="image_credit"
                    value={formData.image_credit}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reading Time (minutes)</label>
                  <input
                    type="number"
                    name="reading_time"
                    value={formData.reading_time}
                    onChange={handleInputChange}
                    min="1"
                    max="60"
                    className={formErrors.reading_time ? 'field-error' : ''}
                  />
                  {formErrors.reading_time && <div className="error-message">{formErrors.reading_time}</div>}
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                    />
                    Featured Post
                  </label>
                </div>
              </div>

              <div className="form-section">
                <div className="seo-header" onClick={() => setShowSeoSettings(!showSeoSettings)}>
                  <h3>SEO Settings</h3>
                  <span className="toggle-icon">{showSeoSettings ? '−' : '+'}</span>
                </div>
                
                {showSeoSettings && (
                  <div className="seo-content">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Title</label>
                        <input
                          type="text"
                          name="meta_title"
                          value={formData.meta_title}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Description</label>
                        <textarea
                          name="meta_description"
                          value={formData.meta_description}
                          onChange={handleInputChange}
                          rows="2"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meta Keywords</label>
                        <input
                          type="text"
                          name="meta_keywords"
                          value={formData.meta_keywords}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">Update Post</button>
                <button type="button" className="btn-cancel" onClick={() => { setShowEditModal(false); resetForm(); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;